import { routeMillGantt } from "@/setup/routes/mill.routes";
import { ErrorModal, useShiftNavigator } from "@dexteel/mesf-core";
import { Card, CardContent, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import { useMemo, useState } from "react";
import { PartCampaignData } from "../../models/PartCampaingData";
import { getPartCampaignData } from "../../repositories/PartsReportRepository";

export const usePartCampaignData = ({
	partId,
	start,
	end,
	onError,
}: {
	partId: number;
	start?: Date | undefined;
	end?: Date | undefined;
	onError: (error: Error) => void;
}) => {
	return useQuery<PartCampaignData[], Error>({
		queryKey: ["partCampaignData", partId, start, end],
		queryFn: ({ signal }) => {
			if (partId) {
				return getPartCampaignData({ partId, start, end }, signal);
			}
			return [];
		},
		onError: onError,
	});
};

export const PartCampaignChart = ({
	partId,
	start,
	end,
}: {
	partId: number;
	start?: Date;
	end?: Date;
}) => {
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const [error, setError] = useState("");

	const { data: partCampaignData, isLoading } = usePartCampaignData({
		partId,
		start,
		end,
		onError: (e) => setError(e.message),
	});

	const handleDateClick = async (params: any) => {
		if (params.componentType === "series") {
			const clickedDate = params.data[0];
			if (clickedDate) {
				const clickedAsDate = new Date(clickedDate);
				const newShift = await getShiftDataFromAPI(clickedAsDate, 0);
				if (newShift) {
					window.open(`${routeMillGantt}/${newShift.CurrentShiftId}`, "_blank");
				}
			}
		}
	};

	const processedData = useMemo(() => {
		if (!partCampaignData || partCampaignData.length === 0) {
			return [];
		}

		// Convert data to format expected by ECharts: [timestamp, value]
		return partCampaignData
			.map((item) => [
				new Date(item.Timestamp).getTime(), // Convert to timestamp for ECharts time axis
				item.CampaignWeight || 0,
			])
			.sort((a, b) => a[0] - b[0]); // Sort by timestamp
	}, [partCampaignData]);

	const option = {
		tooltip: {
			trigger: "axis",
			formatter: function (params: any) {
				if (params && params.length > 0) {
					const date = new Date(params[0].value[0]).toLocaleDateString();
					const weight = params[0].value[1].toFixed(2);
					return `${date}<br/>Campaign Weight: ${weight} tons`;
				}
				return "";
			},
		},
		xAxis: {
			type: "time",
			name: "Date",
			nameLocation: "middle",
			nameGap: 25,
			axisLabel: {
				fontSize: 10,
			},
		},
		yAxis: {
			type: "value",
			name: "Campaign Weight (tons)",
			nameLocation: "middle",
			nameGap: 40,
			axisLabel: {
				fontSize: 10,
				formatter: (value: number) => {
					if (value >= 1000000) {
						return (value / 1000000).toFixed(1) + "M";
					} else if (value >= 1000) {
						return (value / 1000).toFixed(0) + "K";
					} else {
						return value.toFixed(1);
					}
				},
			},
		},
		series: [
			{
				name: "Campaign Weight",
				type: "line",
				smooth: true,
				symbol: "circle",
				symbolSize: 4,
				lineStyle: {
					color: "#2196F3",
					width: 2,
				},
				itemStyle: {
					color: "#2196F3",
				},
				areaStyle: {
					color: {
						type: "linear",
						x: 0,
						y: 0,
						x2: 0,
						y2: 1,
						colorStops: [
							{ offset: 0, color: "rgba(33, 150, 243, 0.3)" },
							{ offset: 1, color: "rgba(33, 150, 243, 0.1)" },
						],
					},
				},
				data: processedData,
			},
		],
		grid: {
			top: 40,
			left: 40,
			containLabel: true,
			height: "80%",
			width: "90%",
		},
	};

	return (
		<>
			<Card
				sx={{
					width: "100%",
					height: "100%",
					overflow: "visible",
				}}
				elevation={1}
			>
				<CardContent
					sx={{
						padding: "2px !important",
					}}
				>
					{isLoading ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "100%",
								width: "100%",
							}}
						>
							<CircularProgress size="small" />
						</div>
					) : (
						<ReactECharts
							option={option}
							onEvents={{
								click: handleDateClick,
							}}
							style={{ cursor: "pointer", width: "100%" }}
						/>
					)}
				</CardContent>
			</Card>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
